import { AppRoutePermission } from "./types";

/**
 * Permissions configuration object for application routes.
 * 
 * This object maps route keys to their respective `AppRoutePermission` settings,
 * specifying which application routes (e.g., **'storage'**, **'transport'**, **'corporate'**) and access rights 
 * (roles/privileges) are allowed to access each route.
 * 
 * Access Rights Mapping:
 * 
 *   [0] - Hezky Admin
 * --------------------------------------------
 *   [1] - Super Admin
 * --------------------------------------------
 *   [2] - Admin
 * --------------------------------------------
 *   [3] - Viewer
 * --------------------------------------------
 *   [4] - Security
 * --------------------------------------------
 *   [5] - Originator
 * --------------------------------------------
 *   [6] - CDQA
 * --------------------------------------------
 *   [7] - RDM
 * --------------------------------------------
 * 
 * @type {Record<string, AppRoutePermission>}
 */

export const Module: Record<string, AppRoutePermission> = {
    // * STORAGE ONLY ROUTES
    assets: {
        allowedProducts: ['storage'],
        accessRight: [0, 1, 2, 3, 5, 6, 7]
    },
    storageIncidents: {
        allowedProducts: ['storage'],
        accessRight: [0, 1, 2, 3, 5, 6, 7]
    },

    // * TRANSPORT ONLY ROUTES
    trips: {
        allowedProducts: ['transport'],
        accessRight: [0, 1, 2, 3, 5, 6, 7]
    },
    transportIncidents: {
        allowedProducts: ['transport'],
        accessRight: [0, 1, 2, 3, 5, 6, 7]
    },
    inventoryManagement: {
        allowedProducts: ['transport'],
        accessRight: [0, 1, 2]
    },
    logisticManagement: {
        allowedProducts: ['transport'],
        accessRight: [0, 1, 2]
    },
    transportLocationManagement: {
        allowedProducts: ['transport'],
        accessRight: [0, 1, 2]
    },
    quickLocationManagement: {
        allowedProducts: ['transport'],
        accessRight: [0, 1, 2]
    },

    // * COMMON ROUTES #########################################################

    // * DASHBOARD ROUTES
    dashboard: {
        allowedProducts: ['corporate', 'storage', 'transport'],
        accessRight: [0, 1, 2, 3, 4, 5, 6, 7]
    },
    corporateDashboard: {
        allowedProducts: ['corporate'],
    },
    storageDashboard: {
        allowedProducts: ['storage'],
    },
    transportDashboard: {
        allowedProducts: ['transport'],
        accessRight: [0, 1, 2, 3, 5, 6, 7]
    },

    // * REPORTS ROUTES
    reports: {
        allowedProducts: ['storage', 'transport'],
        accessRight: [0, 1, 2, 5, 6, 7]
    },
    auditLogReport: {
        allowedProducts: ['storage', 'transport'],
        accessRight: [0, 1, 2]
    },
    // storage only routes
    deviceReport: {
        allowedProducts: ['storage'],
        accessRight: [0, 1, 2, 5, 6, 7]
    },
    assetReport: {
        allowedProducts: ['storage'],
        accessRight: [0, 1, 2, 5, 6, 7]
    },
    dailyReport: {
        allowedProducts: ['storage'],
        accessRight: [0, 1, 2, 5, 6, 7]
    },
    incidentReport: {
        allowedProducts: ['storage'],
        accessRight: [0, 1, 2, 5, 6, 7]
    },
    // transport only routes
    tripSummaryReport: {
        allowedProducts: ['transport'],
        accessRight: [0, 1, 2, 5, 6, 7]
    },
    transporterReport: {
        allowedProducts: ['transport'],
        accessRight: [0, 1, 2, 5, 6, 7]
    },
    dispatchDelayReport: {
        allowedProducts: ['transport'],
        accessRight: [0, 1, 2, 5, 6, 7]
    },
    transportIncidentReport: {
        allowedProducts: ['transport'],
        accessRight: [0, 1, 2, 5, 6, 7]
    },

    // * ADMIN ROUTES
    admin: {
        allowedProducts: ['storage', 'transport'],
        accessRight: [0, 1, 2]
    },
    alarmManagement: {
        allowedProducts: ['storage', 'transport'],
        accessRight: [0, 1, 2]
    },
    deviceManagement: {
        allowedProducts: ['storage', 'transport'],
        accessRight: [0]
    },
    userManagement: {
        allowedProducts: ['storage', 'transport'],
        accessRight: [0, 1, 2]
    },
    productManagement: {
        allowedProducts: ['storage', 'transport'],
        accessRight: [0, 1, 2]
    },
    locationManagement: {
        allowedProducts: ['storage', 'transport'],
        accessRight: [0, 1]
    }
};

/**
 * Permissions configuration object for Incident Forms.
 * 
 * This object maps privilege keys to their respective `boolean[]` settings,
 * specifying which forms (e.g., **'prod'**, **'capa'**, **'eval'**, **'history'**) are allowed to be enabled or disabled based on the privilege.
 * 
 * Access Rights Mapping:
 * 
 *   [0] - Hezky Admin
 * --------------------------------------------
 *   [1] - Super Admin
 * --------------------------------------------
 *   [2] - Admin
 * --------------------------------------------
 *   [3] - Viewer
 * --------------------------------------------
 *   [4] - Security
 * --------------------------------------------
 *   [5] - Originator
 * --------------------------------------------
 *   [6] - CDQA
 * --------------------------------------------
 *   [7] - RDM
 * --------------------------------------------
 * 
 * @type {Record<number, boolean[]>}
 */
export const TABS_PRIVILEGE_MATRIX: { [key: number]: boolean[] } = {
    // false = allow form, true = disable form
    // index 0 = prod, 1 = capa, 2 = eval, 3 = history
    0: [false, false, false, false],    // Hezky Admin: All disabled
    1: [false, false, false, false],    // SuperAdmin: All disabled
    2: [false, false, false, false],    // Admin: All disabled
    3: [true, true, true, true],        // Viewer - All enabled
    4: [false, false, false, false],    // Security - All enabled
    5: [false, false, true, true],      // Originator
    6: [false, false, false, true],     // CDQA
    7: [true, true, true, true],       // RDM
};

/**
 * Permissions configuration object for Incident Forms Buttons.
 * 
 * This object maps privilege keys to their respective `{ submit: boolean, approve: boolean, reject: boolean, product?: boolean }[]` settings,
 * specifying which buttons (e.g., **'submit'**, **'approve'**, **'reject'**) are allowed to be enabled or disabled based on the privilege.
 * 
 * Access Rights Mapping:
 * 
 *   [0] - Hezky Admin
 * --------------------------------------------
 *   [1] - Super Admin
 * --------------------------------------------
 *   [2] - Admin
 * --------------------------------------------
 *   [3] - Viewer
 * --------------------------------------------
 *   [4] - Security
 * --------------------------------------------
 *   [5] - Originator
 * --------------------------------------------
 *   [6] - CDQA
 * --------------------------------------------
 *   [7] - RDM
 * --------------------------------------------
 * 
 * @type {Record<number, { submit: boolean, approve: boolean, reject: boolean, product?: boolean }[]>}
 */
export const BUTTONS_PRIVILEGE_MATRIX: { [key: number]: { submit: boolean, approve: boolean, reject: boolean, product?: boolean }[] } = {
    0: [
        { submit: true, approve: true, reject: true, product: true },
        { submit: true, approve: true, reject: true },
        { submit: true, approve: true, reject: true, product: true }],
    1: [
        { submit: true, approve: true, reject: true, product: true },
        { submit: true, approve: true, reject: true },
        { submit: true, approve: true, reject: true, product: true }
    ],
    2: [
        { submit: true, approve: true, reject: true, product: true },
        { submit: true, approve: true, reject: true },
        { submit: true, approve: true, reject: true, product: true }
    ],
    3: [
        { submit: false, approve: false, reject: false, product: false },
        { submit: false, approve: false, reject: false },
        { submit: false, approve: false, reject: false, product: false }
    ],
    4: [
        { submit: false, approve: false, reject: false, product: false },
        { submit: false, approve: false, reject: false },
        { submit: false, approve: false, reject: false, product: false }],
    5: [
        { submit: true, approve: false, reject: false, product: true },
        { submit: true, approve: false, reject: false },
        { submit: false, approve: false, reject: false, product: false },
    ],
    6: [
        { submit: true, approve: true, reject: true, product: true },
        { submit: true, approve: true, reject: true },
        { submit: true, approve: true, reject: true, product: true },
    ],
    7: [
        { submit: false, approve: false, reject: false, product: false },
        { submit: false, approve: false, reject: false },
        { submit: false, approve: true, reject: true, product: false },
    ],
};